// @migration-in-progress
/**
 * Audit Export Module - PDF generation for audit logs and compliance records
 * 
 * Provides WBC3-compliant audit trail PDF exports with proper formatting
 * and immutable record documentation as required by maritime regulations
 */

import { getAuditLog, AUDIT_ACTIONS, AUDIT_MODULES } from '../audit';
import { debug } from '../logging';
import {
  createPDF,
  getPageDimensions,
  setFont,
  checkPageBreak,
  addReportHeader,
  addReportFooter,
  addSectionHeading,
  addWrappedText,
  formatDateTime,
  savePDF,
  handlePDFError,
  PDF_CONFIG
} from './pdfCore';

/**
 * Export audit log as PDF with filtering options
 * @param {Object} options - Export options
 * @param {Object} [options.filters] - Audit log filters (module, action, dateRange, etc.)
 * @param {string} [options.vesselName] - Vessel name for report header
 * @param {string} [options.category] - Vessel category for compliance context
 * @param {string} [options.reportTitle] - Custom report title
 * @returns {boolean} Success status
 */
export function exportAuditLogPDF(options = {}) {
  try {
    const {
      filters = {},
      vesselName = 'Unknown Vessel',
      category = 'Unknown Category',
      reportTitle = 'Audit Trail Report'
    } = options;

    // Get filtered audit data
    const auditEntries = getAuditLog(filters);
    
    if (auditEntries.length === 0) {
      alert('No audit entries found for the selected criteria.');
      return false;
    }

    // Create PDF document
    const pdf = createPDF();
    const { margin, contentWidth } = getPageDimensions(pdf);
    
    let currentY = margin;

    // Header function for page breaks
    const addHeader = () => {
      return addReportHeader(pdf, margin, {
        title: reportTitle,
        vesselName,
        category,
        filters
      });
    };

    // Add initial header
    currentY = addHeader();

    // Summary information
    setFont(pdf, 'body');
    pdf.text(`Total Entries: ${auditEntries.length}`, margin, currentY);
    currentY += PDF_CONFIG.sectionSpacing;

    // WBC3 Compliance Notice
    currentY = addSectionHeading(pdf, currentY, 'WBC3 Compliance Notice', 'subheading');
    
    const complianceText = 'This audit trail complies with Workboat Code Edition 3 Section 8.3.4 requirements for record keeping and audit documentation. All entries are immutable once created.';
    currentY = addWrappedText(pdf, currentY, complianceText, { fontType: 'body' });
    currentY += PDF_CONFIG.sectionSpacing;

    // Audit Entries Section
    currentY = addSectionHeading(pdf, currentY, 'Audit Entries', 'heading');

    // Process each audit entry
    auditEntries.forEach((entry, index) => {
      const pageBreak = checkPageBreak(pdf, currentY, addHeader, 30);
      currentY = pageBreak.currentY;

      // Entry header
      setFont(pdf, 'subheading');
      pdf.text(`${index + 1}. ${entry.summary}`, margin, currentY);
      currentY += PDF_CONFIG.lineHeight;

      // Entry details
      setFont(pdf, 'small');
      
      const entryDate = new Date(entry.timestamp);
      pdf.text(`Date/Time: ${formatDateTime(entry.timestamp)}`, margin + 5, currentY);
      currentY += PDF_CONFIG.lineHeight;
      
      pdf.text(`Module: ${entry.module}`, margin + 5, currentY);
      currentY += PDF_CONFIG.lineHeight;
      
      pdf.text(`Action: ${entry.action.toUpperCase()}`, margin + 5, currentY);
      currentY += PDF_CONFIG.lineHeight;
      
      pdf.text(`Target ID: ${entry.targetId}`, margin + 5, currentY);
      currentY += PDF_CONFIG.lineHeight;
      
      pdf.text(`User: ${entry.userId}`, margin + 5, currentY);
      currentY += PDF_CONFIG.lineHeight;

      // Additional details if present
      if (entry.details && Object.keys(entry.details).length > 0) {
        pdf.text('Details:', margin + 5, currentY);
        currentY += PDF_CONFIG.lineHeight;
        
        Object.entries(entry.details).forEach(([key, value]) => {
          if (value !== null && value !== undefined && value !== '') {
            const detailText = `  ${key}: ${value}`;
            currentY = addWrappedText(pdf, currentY, detailText, { 
              fontType: 'small', 
              indent: 10,
              maxWidth: contentWidth - 10 
            });
          }
        });
      }

      // Vessel context if different from header
      if (entry.context && entry.context.vesselName && entry.context.vesselName !== vesselName) {
        pdf.text(`Vessel Context: ${entry.context.vesselName}`, margin + 5, currentY);
        currentY += PDF_CONFIG.lineHeight;
      }

      currentY += PDF_CONFIG.sectionSpacing / 2; // Space between entries
    });

    // Add footer
    addReportFooter(pdf, {
      customText: 'WBC3 Compliant Audit Trail - Immutable Record'
    });

    // Save the PDF
    const filename = savePDF(pdf, 'fleetskipper_audit_report', new Date());
    
    debug.log('PDF export completed', { 
      filename, 
      entryCount: auditEntries.length,
      filters: filters
    });
    
    return true;
    
  } catch (error) {
    handlePDFError(error, 'audit log export');
    return false;
  }
}

/**
 * Export current audit log with vessel information from localStorage
 * @param {Object} filters - Audit log filters
 * @returns {boolean} Success status
 */
export function exportCurrentAuditLog(filters = {}) {
  // Get vessel info from localStorage if available
  const vesselData = JSON.parse(localStorage.getItem('currentVessel') || '{}');
  
  return exportAuditLogPDF({
    filters,
    vesselName: vesselData.name || 'Current Vessel',
    category: vesselData.category || 'Unknown Category'
  });
}

/**
 * Get available export formats for audit logs
 * @returns {Array} Array of supported export formats
 */
export function getSupportedExportFormats() {
  return [
    { 
      key: 'pdf', 
      label: 'PDF Report', 
      description: 'Professional PDF report with WBC3 compliance formatting' 
    },
    { 
      key: 'json', 
      label: 'JSON Data', 
      description: 'Raw data export for system integration' 
    },
    { 
      key: 'csv', 
      label: 'CSV Spreadsheet', 
      description: 'Spreadsheet format for data analysis' 
    }
  ];
}